import Keycloak from 'keycloak-js'

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8180/auth',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'okr-saas',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'okr-frontend',
}

export const keycloak = new Keycloak(keycloakConfig)

let initPromise: Promise<boolean> | null = null

export const initKeycloak = async (): Promise<boolean> => {
  // Prevent multiple initializations (React StrictMode calls useEffect twice)
  if (initPromise) {
    return initPromise
  }

  initPromise = (async () => {
    try {
      const authenticated = await keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        pkceMethod: 'S256',
        // check-session iframe compares SSO state with keycloak.caisibo.com via
        // cross-origin postMessage on every updateToken() call. Frontend and
        // Keycloak live on different subdomains, so this handshake is flaky
        // (tracking protection, cookie partitioning) — any non-'unchanged'
        // reply, including 'error', wipes an otherwise-valid token. PKCE
        // already secures the flow and we don't need cross-tab logout sync.
        checkLoginIframe: false,
        // This realm/client does not put a `nonce` claim on the access/refresh
        // token (only the ID token, per OIDC core spec). keycloak-js's nonce
        // check compares storedNonce against all three token types, so it
        // always fails here even though the ID token's nonce is correct.
        // PKCE (S256) already protects the authorization code exchange, so
        // nonce validation is redundant defense-in-depth for this flow.
        useNonce: false,
      })
      return authenticated
    } catch (error) {
      console.error('Keycloak initialization failed:', error)
      initPromise = null // Reset on error to allow retry
      return false
    }
  })()

  return initPromise
}

export const getToken = (): string | undefined => {
  return keycloak.token
}

export const refreshToken = async (): Promise<boolean> => {
  try {
    // -1 forces keycloak-js to always request a new token rather than only
    // when the current one is near expiry. The realm's access token lifespan
    // (1800s) is far longer than the ~90s bound we need for a role revoke to
    // reach the app, so a proximity check alone would leave a stale token in
    // place for most of that lifespan.
    const refreshed = await keycloak.updateToken(-1)
    return refreshed
  } catch (error) {
    console.error('Token refresh failed:', error)
    return false
  }
}

export const logout = (): void => {
  keycloak.logout({
    redirectUri: window.location.origin,
  })
}

export const hasRole = (role: string): boolean => {
  return keycloak.hasRealmRole(role)
}

export const getUserInfo = () => {
  const roles = keycloak.tokenParsed?.realm_access?.roles || []
  return {
    id: keycloak.subject,
    email: keycloak.tokenParsed?.email,
    name: keycloak.tokenParsed?.name,
    firstName: keycloak.tokenParsed?.given_name,
    lastName: keycloak.tokenParsed?.family_name,
    roles,
    tenantId: keycloak.tokenParsed?.tenant_id,
    isOrgAdmin: roles.includes('org_admin') || roles.includes('super_admin'),
  }
}
